import type { BrandConfig } from '@bv/api/brand';
import { HEALTH_CHECK_ROUTE_NAME, HEALTH_CHECK_ROUTE_PATH } from '@bv/constants/maintenance/health-check';
import { MAINTENANCE_ROUTE_NAME, MAINTENANCE_ROUTE_PATH } from '@bv/constants/maintenance/page';

/** How often an open tab re-reads `maintenanceMode`. */
const MAINTENANCE_POLL_INTERVAL_MS = 2 * 60 * 1000;

/** Storybook and other hosts that pass `brand: false` skip the domain-brand read. */
let syncMaintenanceMode = true;

/** One shared read for the first navigation of this page load. */
let maintenanceSample: Promise<void> | null = null;

/** Set for this page load when `domain-brand` reports `maintenanceMode: true`. */
let apiMaintenanceBlock = false;

/** Page to reopen after this page load leaves the maintenance screen. */
let maintenanceReturnPath: string | null = null;

/**
 * A route the maintenance guard can classify without a full router location.
 */
export type MaintenanceRoute = {
  name?: string | symbol | null;
  path: string;
  fullPath: string;
};

/** Where navigation should go while the maintenance rules are in force. */
export type MaintenanceNavigation = 'allow' | 'block' | 'restore';

type MaintenanceRouter = {
  currentRoute: { value: MaintenanceRoute };
  replace: (to: { name: string }) => unknown;
  isReady?: () => Promise<void>;
};

/**
 * Whether `domain-brand` says the backend is out of maintenance.
 * Only a strict `false` counts.
 * @param config - Brand payload, or nothing when the call failed.
 */
export const isDomainBrandReady = (config?: Pick<BrandConfig, 'maintenanceMode'> | null): boolean =>
  config?.maintenanceMode === false;

/**
 * Turns the domain-brand read on or off before the router starts navigating.
 * @param enabled - `false` for hosts that do not talk to domain-brand.
 */
export const setMaintenanceModeSyncEnabled = (enabled: boolean): void => {
  syncMaintenanceMode = enabled;
};

/**
 * Whether this page load's latest `domain-brand` response said `maintenanceMode: true`.
 * Blocks until a later response says `false`. A refresh starts clear.
 */
export const isApiMaintenanceBlock = (): boolean => apiMaintenanceBlock;

/**
 * Records the latest `maintenanceMode` for this page load.
 * `true` blocks immediately. `false` lifts that block.
 * Any other payload leaves the flag unchanged.
 * @param config - Brand payload, or nothing when the call failed.
 */
export const applyDomainBrandMaintenance = (config?: Pick<BrandConfig, 'maintenanceMode'> | null): void => {
  if (config?.maintenanceMode === true) {
    apiMaintenanceBlock = true;
    return;
  }

  if (!isDomainBrandReady(config)) {
    return;
  }

  apiMaintenanceBlock = false;
};

/**
 * Whether the blocking screen should be showing.
 * Only a `maintenanceMode: true` from `domain-brand` during this page load blocks.
 */
export const shouldBlockForMaintenance = (): boolean => isApiMaintenanceBlock();

const isMaintenanceRoute = (route: MaintenanceRoute): boolean =>
  route.name === MAINTENANCE_ROUTE_NAME || route.path === MAINTENANCE_ROUTE_PATH;

const isHealthCheckRoute = (route: MaintenanceRoute): boolean =>
  route.name === HEALTH_CHECK_ROUTE_NAME || route.path === HEALTH_CHECK_ROUTE_PATH;

/**
 * Classifies a navigation against the latest `maintenanceMode`.
 * `restore` means leave the maintenance screen and reload the app.
 * @param route - Destination route.
 */
export const maintenanceAccessDecision = (route: MaintenanceRoute): MaintenanceNavigation => {
  if (isHealthCheckRoute(route)) {
    return 'allow';
  }

  const blocking = shouldBlockForMaintenance();

  if (isMaintenanceRoute(route)) {
    return blocking ? 'allow' : 'restore';
  }

  return blocking ? 'block' : 'allow';
};

const isSafeReturnPath = (fullPath: string): boolean =>
  fullPath.startsWith('/') &&
  !fullPath.startsWith('//') &&
  fullPath !== MAINTENANCE_ROUTE_PATH &&
  !fullPath.startsWith(`${MAINTENANCE_ROUTE_PATH}?`) &&
  !fullPath.startsWith(`${MAINTENANCE_ROUTE_PATH}#`);

/**
 * Remembers the page the user was opening so a later reload can return to it.
 * Kept for this page load only. A refresh falls back to `/`.
 * @param fullPath - Router full path, including query and hash.
 */
export const rememberMaintenanceReturnPath = (fullPath: string): void => {
  if (!isSafeReturnPath(fullPath)) {
    return;
  }

  maintenanceReturnPath = fullPath;
};

/**
 * Reloads into the app so bootstrap runs again. Uses `/` when no return path was stored.
 */
export const restoreFromMaintenance = (): void => {
  const stored = maintenanceReturnPath;
  maintenanceReturnPath = null;

  const path = stored && isSafeReturnPath(stored) ? stored : '/';
  window.location.assign(path);
};

/**
 * Reads `maintenanceMode` from the brand store and stores it.
 * A failed call leaves the flags unchanged.
 */
const sampleDomainBrand = async (): Promise<void> => {
  try {
    const { useBrandStore } = await import('@bv/stores/brand');
    const config = await useBrandStore().fetchConfig();
    applyDomainBrandMaintenance(config);
  } catch {
    // Leave the flags unchanged. The next poll will try again.
  }
};

/**
 * Shares the first `domain-brand` read across the guard and the open-tab sync.
 */
const ensureMaintenanceSampled = (): Promise<void> => {
  if (!maintenanceSample) {
    maintenanceSample = sampleDomainBrand();
  }

  return maintenanceSample;
};

/**
 * Waits for the first `domain-brand` read of this page load when sync is enabled.
 * Hosts that disable brand setup resolve immediately.
 */
export const syncMaintenanceModeFromBrand = (): Promise<void> => {
  if (!syncMaintenanceMode) {
    return Promise.resolve();
  }

  return ensureMaintenanceSampled();
};

const applyMaintenanceRoute = (router: MaintenanceRouter, route: MaintenanceRoute): void => {
  const decision = maintenanceAccessDecision(route);

  if (decision === 'block') {
    rememberMaintenanceReturnPath(route.fullPath);
    void router.replace({ name: MAINTENANCE_ROUTE_NAME });
    return;
  }

  if (decision === 'restore') {
    restoreFromMaintenance();
  }
};

/**
 * Applies the maintenance decision for one navigation.
 * The first call of a page load waits for `domain-brand`, except on the health check.
 * `null` means the caller should continue with its own guard.
 * @param route - Destination route.
 */
export const settleMaintenanceNavigation = async (
  route: MaintenanceRoute,
): Promise<{ name: typeof MAINTENANCE_ROUTE_NAME } | 'reload' | null> => {
  if (syncMaintenanceMode && !isHealthCheckRoute(route)) {
    await ensureMaintenanceSampled();
  }

  const decision = maintenanceAccessDecision(route);

  if (decision === 'block') {
    rememberMaintenanceReturnPath(route.fullPath);
    return { name: MAINTENANCE_ROUTE_NAME };
  }

  if (decision === 'restore') {
    return 'reload';
  }

  return null;
};

/**
 * Re-reads `maintenanceMode` while this tab is open and moves to or from the maintenance screen.
 * Polling starts only when the first read reports `maintenanceMode: true`, and stops when a later read reports `false`.
 * The first read is the same one the navigation guard waits for.
 * @param router - Router that owns the maintenance route.
 */
export const startMaintenanceModeSync = (router: MaintenanceRouter): void => {
  if (!syncMaintenanceMode) {
    return;
  }

  let polling = false;
  let pollTimer = 0;
  let onVisibilityChange: (() => void) | null = null;

  const stopPolling = (): void => {
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = 0;
    }

    if (onVisibilityChange) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      onVisibilityChange = null;
    }
  };

  const pollDomainBrand = async (): Promise<void> => {
    if (polling || document.visibilityState !== 'visible') {
      return;
    }

    polling = true;

    try {
      await sampleDomainBrand();

      if (!isApiMaintenanceBlock()) {
        stopPolling();
      }

      applyMaintenanceRoute(router, router.currentRoute.value);
    } finally {
      polling = false;
    }
  };

  const begin = (): void => {
    void ensureMaintenanceSampled().then(() => {
      applyMaintenanceRoute(router, router.currentRoute.value);

      if (!isApiMaintenanceBlock()) {
        return;
      }

      pollTimer = window.setInterval(() => {
        void pollDomainBrand();
      }, MAINTENANCE_POLL_INTERVAL_MS);

      onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
          void pollDomainBrand();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
    });
  };

  if (router.isReady) {
    void router.isReady().then(begin);
    return;
  }

  begin();
};
